import { useRef, useState } from "react";
import { FiX } from "react-icons/fi";

interface InlineTagEditorProps {
  tags: string[];
  onSave: (tags: string[]) => void;
}

export function InlineTagEditor({ tags: initialTags, onSave }: InlineTagEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [tagInput, setTagInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAddTag = () => {
    const newTag = tagInput.trim();
    if (newTag && !tags.includes(newTag)) {
      setTags([...tags, newTag]);
    }
    setTagInput("");
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSave = () => {
    // Add any pending input as a tag
    let finalTags = tags;
    if (tagInput.trim()) {
      const newTag = tagInput.trim();
      if (!tags.includes(newTag)) {
        finalTags = [...tags, newTag];
        setTags(finalTags);
      }
      setTagInput("");
    }

    const tagsChanged =
      finalTags.length !== initialTags.length || finalTags.some((tag, i) => tag !== initialTags[i]);

    if (tagsChanged) {
      onSave(finalTags);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddTag();
    } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
      setTags(tags.slice(0, -1));
    } else if (e.key === "Escape") {
      setTags(initialTags);
      setTagInput("");
      setIsEditing(false);
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (isEditing) {
    return (
      <div
        className="flex flex-wrap items-center gap-1 rounded border border-blue-500 bg-white p-1 min-h-[32px]"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-0.5 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
          >
            {tag}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTag(tag);
              }}
              className="ml-0.5 rounded-full p-0.5 hover:bg-blue-200"
            >
              <FiX className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          placeholder={tags.length === 0 ? "Add tags..." : ""}
          className="flex-1 min-w-[60px] border-none bg-transparent px-1 py-0.5 text-xs focus:outline-none"
        />
      </div>
    );
  }

  return (
    <div
      onClick={startEditing}
      className="cursor-text rounded py-1 hover:bg-gray-100 min-h-[32px] flex items-center"
      title="Click to edit tags"
    >
      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : (
        <span className="text-sm text-gray-400">Click to add</span>
      )}
    </div>
  );
}
